































import Vue, { PropType } from 'vue';

import Logger from '@/common/utils/logger';
import { SET_ERROR_BANNER } from '@/store/general/mutations';

import ScoreVisualization, { ScoreConfig } from '@/views/reports/visualizations/scores/ScoreVisualization.vue';
import ReferenceScoreVisualization from '@/views/reports/visualizations/scores/ReferenceScoreVisualization.vue';

import { ReportData } from '@/common/types/reports/reportData';

import AssessmentsApi from '@/api/assessment.api';

import InfoModal from '@/views/common/containers/InfoModal.vue';
import { TranslateResult } from 'vue-i18n';
import { ComparativeReportScore } from '@/common/types/reports/assessment';

export default Vue.extend({
    components: {
        ReferenceScoreVisualization,
        ScoreVisualization,
        InfoModal,
    },
    props: {
        reportData: Object as PropType<ReportData>,
        reportVersion: Number,
    },
    data() {
        return {
            config: undefined as ScoreConfig | undefined,
            comparativeReportScore: undefined as ComparativeReportScore | undefined,
        };
    },
    computed: {
        infoText(): TranslateResult[] {
            const infoText = [] as TranslateResult[];

            if (this.config && this.config.reportVersion === 1) {
                infoText.push(this.$t('reports.info.myScore'));
                infoText.push(this.$t('reports.info.dynamicVisionScale'));
                infoText.push(this.$t('reports.info.eyeMovementScores'));
                infoText.push(this.$t('reports.info.percentiles'));
                infoText.push(this.$t('reports.info.referenceRange'));
            } else {
                infoText.push(this.$t('reports.info.dynamicVisionV2.dynamicVisionScore'));
                infoText.push(this.$t('reports.info.dynamicVisionV2.eyeMovementScores'));
                infoText.push(this.$t('reports.info.dynamicVisionV2.eyeMovementScorePursuit'));
                infoText.push(this.$t('reports.info.dynamicVisionV2.eyeMovementScoreSaccade'));
                infoText.push(this.$t('reports.info.dynamicVisionV2.eyeMovementScoreFixation'));
                infoText.push(this.$t('reports.info.dynamicVisionV2.eyeMovementScoreFooterOne'));
                infoText.push(this.$t('reports.info.dynamicVisionV2.eyeMovementScoreFooterTwo'));
            }

            return infoText;
        },
        isParticipantAssessment(): boolean {
            if (this.reportData.assessment?.id) {
                return true;
            } else {
                return false;
            }
        },
    },
    async mounted() {
        try {
            if (this.isParticipantAssessment) {
                const score = await AssessmentsApi.getReportScore(
                    this.reportData.assessment.id,
                    this.reportData.assessmentType.name,
                );
                const csp = this.reportData.tests.circularSmoothPursuit;
                const fixation = this.reportData.tests.fixationStability;
                const hs = this.reportData.tests.horizontalSaccades;

                if (csp === null || fixation === null || hs === null) {
                    throw new Error(
                        'We could not generate a report because you did not successfully complete all required tests. Please retest.',
                    );
                }
                const cspMetrics = csp.metrics;
                const onTargetSp = cspMetrics.both.onTargetSpPercent;
                const spPercent = cspMetrics.both.spPercent;

                const cspAccuracy = Math.floor((onTargetSp / spPercent) * 100.0);

                const fixationMetrics = fixation.metrics;
                const fixationBand1 = fixationMetrics.both.gazePositionsBand1Aggregate;
                const fixationBand2 = fixationMetrics.both.gazePositionsBand2Aggregate;
                const fixationAccuracy = Math.floor(fixationBand1 + fixationBand2);

                const hsMetrics = hs.metrics;
                const hsLeftMissed = hsMetrics.left.missed;
                const hsRightMissed = hsMetrics.right.missed;

                const hsLeftSaccadeNum = hsMetrics.left.saccadeNum.value;
                const hsRightSaccadeNum = hsMetrics.right.saccadeNum.value;

                const leftAccuracy = (hsLeftSaccadeNum - hsLeftMissed) / hsLeftSaccadeNum;
                const rightAccuracy = (hsRightSaccadeNum - hsRightMissed) / hsRightSaccadeNum;
                const saccadeAccuracy = Math.floor(((leftAccuracy + rightAccuracy) / 2.0) * 100.0);

                const config = {} as ScoreConfig;
                config.overall = score.overallScore;
                config.saccades = score.saccadesScore;
                config.pursuits = score.pursuitsScore;
                config.fixations = score.fixationsScore;
                config.saccadesPercentile = score.saccadesPercentile;
                config.pursuitsPercentile = score.pursuitsPercentile;
                config.fixationsPercentile = score.fixationsPercentile;
                config.functionalWindowLowerBound = score.functionalWindowLowerBound;
                config.functionalWindowUpperBound = score.functionalWindowUpperBound;
                config.overallAccuracy = Math.floor((cspAccuracy + saccadeAccuracy + fixationAccuracy) / 3.0);
                config.saccadeAccuracy = saccadeAccuracy;
                config.pursuitAccuracy = cspAccuracy;
                config.fixationAcurracy = fixationAccuracy;
                config.reportVersion = this.reportVersion;

                this.config = config;
            } else {
                if (this.reportData.info?.id) {
                    this.comparativeReportScore = await AssessmentsApi.getComparativeScoreData(this.reportData.info.id);
                }
            }
        } catch (error) {
            Logger.error(`Failed to load score: ${error}`);
            this.$store.commit(SET_ERROR_BANNER, error.message);
            throw error;
        }
    },
});
